import type { AxiosResponse } from 'axios'

import type {
  TeamProperty,
  TeamPropertyAudioValue,
  TeamPropertyValue,
} from '@/store/types/PropertyTypes'
import { isTeamPropertyAudioValue } from '@/store/types/PropertyTypes'
import { get, post, put, remove } from '@/backend/api'
import type { ParsedError } from '@/backend/error'
import { errorMessages, isErrorResponse, parseError } from '@/backend/error'
import { sendV2Commands } from '@/backend/darwin/sendV2Commands'
import type { AnnotationClassPayload, ApiResponse } from '@/store/types'
import type { V2WorkflowCommandResponse } from '@/store/types/V2WorkflowCommandResponse'
import type { PartialRecord } from '@/core/helperTypes'
import type { ApiResult } from './types'

export const getBaseUrl = (teamSlug: string): string =>
  `/v2/teams/${teamSlug}/properties?include_values=true`
export const getIdUrl = (teamSlug: string, id: string): string =>
  `/v2/teams/${teamSlug}/properties/${id}`

export type DatasetItemProperties = PartialRecord<TeamProperty['id'], DatasetItemProperty>

export type DatasetItemPropertyActor = {
  model_id: string | undefined
  role: string
  user_id: number
}

export type DatasetItemPropertyId = TeamProperty['id']

export type DatasetItemPropertyValues =
  | TeamProperty['property_values'][0]['id'][]
  | [{ text: string }]
  | [TeamPropertyAudioValue]

export type DatasetItemProperty = {
  actors: DatasetItemPropertyActor[]
  id: DatasetItemPropertyId
  values: DatasetItemPropertyValues
}

export type DatasetItemPropertiesResponse = {
  properties: DatasetItemProperty[]
}

export type SnapshotItemPropertiesResponse = DatasetItemPropertiesResponse & {
  /** Every property the snapshot references, with deleted properties and values included */
  property_definitions: TeamProperty[]
  /** Classes of the snapshot's annotations deleted since */
  annotation_classes: AnnotationClassPayload[]
}

type GetPropertiesResponse = {
  properties: TeamProperty[]
}

const isTextPropertyValue = (value: unknown): value is { text: string } =>
  typeof value === 'object' && value !== null && 'text' in value

export const isDatasetItemTextPropertyValue = (
  value: DatasetItemPropertyValues,
): value is [{ text: string }] =>
  Array.isArray(value) && value.length === 1 && isTextPropertyValue(value[0])

export const isDatasetItemAudioPropertyValue = (
  value: DatasetItemPropertyValues,
): value is [TeamPropertyAudioValue] =>
  Array.isArray(value) && value.length === 1 && isTeamPropertyAudioValue(value[0])

/**
 * Predefined (single/multi select) values are arrays of value-id strings,
 * whereas the text and audio variants are single-element arrays of objects —
 * so an all-strings array can only be the predefined variant.
 */
export const isDatasetItemPredefinedPropertyValues = (
  value: DatasetItemPropertyValues,
): value is string[] => Array.isArray(value) && value.every((v) => typeof v === 'string')

export const getTeamProperties = async (
  teamSlug: string,
): Promise<ApiResult<GetPropertiesResponse>> => {
  try {
    const response = await get<GetPropertiesResponse>(getBaseUrl(teamSlug))
    return { ok: true, data: response.data }
  } catch (error) {
    if (!isErrorResponse(error)) {
      throw error
    }
    return { ...parseError(error, errorMessages.PROPERTIES_LOAD), ok: false }
  }
}

export const reorderTeamProperties = async (
  teamSlug: string,
  annotationClassId: string | number,
  propertyIds: string[],
): Promise<ApiResult<GetPropertiesResponse>> => {
  try {
    const response = await post<GetPropertiesResponse>(
      `/v2/teams/${teamSlug}/annotation_classes/${annotationClassId}/properties/reorder`,
      { ids: propertyIds },
    )
    return { ok: true, data: response.data }
  } catch (error) {
    if (!isErrorResponse(error)) {
      throw error
    }
    return { ...parseError(error, errorMessages.PROPERTIES_REORDER), ok: false }
  }
}

export type TeamPropertyRequestBody = Pick<
  TeamProperty,
  | 'name'
  | 'description'
  | 'type'
  | 'required'
  | 'annotation_class_id'
  | 'dataset_ids'
  // Audio duration bounds; nullable so an existing bound can be cleared.
  | 'min_duration_seconds'
  | 'max_duration_seconds'
> & {
  property_values: Omit<TeamPropertyValue, 'id' | 'position'>[]
  granularity?: TeamProperty['granularity']
  /**
   * When provided, the new property is created as a child of the referenced
   * property. Immutable after creation; cannot be set via update.
   */
  parent_property_id?: TeamProperty['parent_property_id']
  /**
   * Visibility condition against the parent. Required when `parent_property_id`
   * is set, must be absent otherwise.
   */
  trigger_condition?: TeamProperty['trigger_condition']
}

export type TeamPropertyUpdateRequestBody = Omit<
  TeamPropertyRequestBody,
  'annotation_class_id' | 'type' | 'granularity' | 'parent_property_id' | 'trigger_condition'
>

// Frame index for a property can be `number` as normal, or the string `global` when granularity
// it set to `annotation`
export const STATIC_PROPERTY_FRAME_NAME = 'global'
export type TeamPropertyFrameIndex = number | typeof STATIC_PROPERTY_FRAME_NAME

export const createTeamProperty = async (
  teamSlug: string,
  params: TeamPropertyRequestBody,
): Promise<AxiosResponse<TeamProperty> | ParsedError> => {
  try {
    const response = await post<TeamProperty>(getBaseUrl(teamSlug), params)
    return response
  } catch (error) {
    if (!isErrorResponse(error)) {
      throw error
    }
    return parseError(error, errorMessages.PROPERTIES_CREATE)
  }
}

export const updateTeamProperty = async (
  teamSlug: TeamProperty['slug'],
  id: TeamProperty['id'],
  params: TeamPropertyUpdateRequestBody,
): Promise<AxiosResponse<TeamProperty> | ParsedError> => {
  try {
    const response = await put<TeamProperty>(getIdUrl(teamSlug, id), params)
    return response
  } catch (error) {
    if (!isErrorResponse(error)) {
      throw error
    }
    return parseError(error, errorMessages.PROPERTIES_UPDATE)
  }
}

export const reorderTeamPropertyValues = async (
  teamSlug: TeamProperty['slug'],
  propertyId: TeamProperty['id'],
  propertyValueIds: TeamPropertyValue['id'][],
): Promise<AxiosResponse<TeamProperty> | ParsedError> => {
  try {
    const response = await post<TeamProperty>(
      `${getIdUrl(teamSlug, propertyId)}/property_values/reorder`,
      {
        ids: propertyValueIds,
      },
    )
    return response
  } catch (error) {
    if (!isErrorResponse(error)) {
      throw error
    }
    return parseError(error, errorMessages.PROPERTIES_VALUES_REORDER)
  }
}

export const deleteTeamProperty = async (
  teamSlug: TeamProperty['slug'],
  id: TeamProperty['id'],
): Promise<AxiosResponse<TeamProperty> | ParsedError> => {
  try {
    const response = await remove<TeamProperty>(getIdUrl(teamSlug, id))
    return response
  } catch (error) {
    if (!isErrorResponse(error)) {
      throw error
    }
    return parseError(error, errorMessages.PROPERTIES_DELETE)
  }
}

export const updateTeamPropertyValue = async (
  teamSlug: TeamProperty['slug'],
  propertyId: TeamProperty['id'],
  propertyValueId: string,
  params: Pick<TeamPropertyValue, 'color' | 'value'>,
): Promise<AxiosResponse<TeamPropertyValue> | ParsedError> => {
  try {
    const response = await put<TeamPropertyValue>(
      `/v2/teams/${teamSlug}/properties/${propertyId}/property_values/${propertyValueId}`,
      params,
    )
    return response
  } catch (error) {
    if (!isErrorResponse(error)) {
      throw error
    }
    return parseError(error, errorMessages.PROPERTIES_VALUE_UPDATE)
  }
}

export const deleteTeamPropertyValue = async (
  teamSlug: TeamProperty['slug'],
  propertyId: TeamProperty['id'],
  propertyValueId: string,
): Promise<AxiosResponse<TeamProperty> | ParsedError> => {
  try {
    const response = await remove<TeamProperty>(
      `/v2/teams/${teamSlug}/properties/${propertyId}/property_values/${propertyValueId}`,
    )
    return response
  } catch (error) {
    if (!isErrorResponse(error)) {
      throw error
    }
    return parseError(error, errorMessages.PROPERTIES_VALUE_DELETE)
  }
}

// The two audio helpers below intentionally return raw data / throw raw axios errors (consumed
// as component error state), matching the getSignedUrl precedent in loadFramesManifests.ts.

/**
 * Requests a presigned upload slot for an audio property recording.
 * Returns the audio id and the signed URL the encoded mp3 should be PUT to.
 */
export const signPropertyAudioUpload = async (
  teamSlug: string,
  itemId: string,
): Promise<{ id: string; upload_url: string }> => {
  const response = await post<{ id: string; upload_url: string }>(
    `/v2/teams/${teamSlug}/items/${itemId}/property_audio`,
    {},
  )
  return response.data
}

/**
 * Fetches a short-lived signed URL for an audio property recording. The
 * response is plain JSON through the normal API pipeline (no redirect, no
 * cross-origin byte fetch), and the returned S3 URL is fed directly to an
 * `<audio>` element as its `src`.
 */
export const getPropertyAudioUrl = async (
  teamSlug: string,
  itemId: string,
  audioId: string,
): Promise<string> => {
  const response = await get<{ url: string }>(
    `/v2/teams/${teamSlug}/items/${itemId}/property_audio/${audioId}/sign`,
  )
  return response.data.url
}

/**
 * Returns the item property values a stage exit snapshot recorded, with the definitions it needs.
 */
export const getSnapshotItemProperties = (
  teamSlug: string,
  itemId: string,
  snapshotId: string,
): Promise<AxiosResponse<SnapshotItemPropertiesResponse>> =>
  get<SnapshotItemPropertiesResponse>(
    `/v2/teams/${teamSlug}/items/${itemId}/snapshots/${snapshotId}/properties`,
  )

/**
 * Returns all the property values that are selected for the given item.
 * The name is a bit confusing but it's the best representation of the data
 */
export const getItemTeamItemProperties = (
  teamSlug: TeamProperty['slug'],
  itemId: string,
  annotationGroupIds?: string[],
): Promise<AxiosResponse<DatasetItemPropertiesResponse>> => {
  const queryParams: { annotation_group_ids?: string[] } = {}

  if (annotationGroupIds && annotationGroupIds.length > 0) {
    queryParams.annotation_group_ids = annotationGroupIds
  }

  return get<DatasetItemPropertiesResponse>(
    `/v2/teams/${teamSlug}/items/${itemId}/properties`,
    queryParams,
    { arrayFormat: 'brackets' },
  )
}

/**
 * Set's the property values for an item
 */
export const setItemTeamPropertyValue = (
  teamSlug: TeamProperty['slug'],
  itemId: string,
  propertyId: DatasetItemPropertyId,
  propertyValueIds: DatasetItemPropertyValues,
  stageInstanceId: string,
  stageId: string,
  annotationGroupId?: string | null,
): Promise<ApiResponse<V2WorkflowCommandResponse> | ParsedError> =>
  sendV2Commands({
    datasetItemId: itemId,
    teamSlug,
    commands: [
      {
        type: 'set_item_properties',
        data: {
          properties: {
            [propertyId]: propertyValueIds,
          },
          stage_id: stageId,
          stage_instance_id: stageInstanceId,
          ...(annotationGroupId !== undefined && { annotation_group_id: annotationGroupId }),
        },
      },
    ],
  })
